import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router';
import { Trophy, Calendar, Trash2, Play, Eye, Filter, ArrowLeft, Map, Target, Users, Gauge, MapPin, User } from 'lucide-react';
import { getGames, getTeams, deleteGame } from '../utils/storage';
import { Game } from '../types/curling';
import { computeSheetScoring } from '../utils/sheetScoring';
import { computePlayerScores, computeTeamScore } from '../utils/playerScoring';
import { getPointsFromResult } from '../utils/analytics';
import { useAuth } from '../auth/AuthContext';

export function GameHistory() {
  const location = useLocation();
  const { user } = useAuth();
  const [games, setGames] = useState<Game[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<string>('all');
  const [selectedClub, setSelectedClub] = useState<string>('all');
  const [myGamesOnly, setMyGamesOnly] = useState(false);
  // Every roster display name linked to the signed-in user, across every team they
  // can see — name-matching, same philosophy the rest of the app already uses for
  // player identity, now powering a convenience filter rather than just a label.
  const [myLinkedNames, setMyLinkedNames] = useState<string[]>([]);

  useEffect(() => {
    loadGames();
    loadMyLinkedNames();
  }, []);

  const loadMyLinkedNames = async () => {
    if (!user) return;
    try {
      const teams = await getTeams();
      const names = new Set<string>();
      teams.forEach(team =>
        team.players.forEach(player => {
          if (player.linkedUserId === user.id) names.add(player.displayName);
        })
      );
      setMyLinkedNames(Array.from(names));
    } catch (error) {
      console.error('Failed to load linked rosters:', error);
    }
  };

  useEffect(() => {
    // Check if we have a prepopulated team filter from navigation state
    if (location.state && (location.state as any).filterTeam) {
      setSelectedTeam((location.state as any).filterTeam);
    }
  }, [location.state]);

  const loadGames = async () => {
    try {
      setGames(await getGames());
    } catch (error) {
      console.error('Failed to load games:', error);
    }
  };

  // Get all unique teams from games
  const allTeams = Array.from(
    new Set(games.flatMap(game => [game.team1Name, game.team2Name]))
  ).sort();

  // Get all unique clubs from games
  const allClubs = Array.from(
    new Set(games.map(game => game.curlingClub).filter((club): club is string => !!club))
  ).sort();

  // Filter games by selected team, club, and optionally "my games", then sort by
  // date (most recent first)
  const filteredGames = games
    .filter(game => selectedTeam === 'all' || game.team1Name === selectedTeam || game.team2Name === selectedTeam)
    .filter(game => selectedClub === 'all' || game.curlingClub === selectedClub)
    .filter(game =>
      !myGamesOnly ||
      game.ends.some(end => end.shots.some(shot => myLinkedNames.includes(shot.playerName)))
    )
    .sort((a, b) => b.date - a.date);

  const handleDeleteGame = async (id: string) => {
    if (confirm('Are you sure you want to delete this game?')) {
      try {
        await deleteGame(id);
        await loadGames();
      } catch (error) {
        console.error('Failed to delete game:', error);
      }
    }
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-950 via-blue-900 to-slate-900">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-white">Game History</h1>
          <Link
            to="/CurlingCodex"
            className="flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 text-white px-4 py-2 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Dashboard
          </Link>
        </div>

        {/* Filter Section */}
        {games.length > 0 && (
          <div className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-2xl shadow-xl shadow-black/20 p-4 mb-6">
            <div className="flex items-center flex-wrap gap-3">
              <Filter className="w-5 h-5 text-blue-300" />

              <label className="text-sm font-medium text-blue-200">Filter by Team:</label>
              <select
                value={selectedTeam}
                onChange={(e) => setSelectedTeam(e.target.value)}
                className="bg-white/10 border border-white/20 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400/60"
              >
                <option value="all" className="bg-blue-950">All Teams ({games.length} games)</option>
                {allTeams.map(team => {
                  const teamGameCount = games.filter(g =>
                    g.team1Name === team || g.team2Name === team
                  ).length;
                  return (
                    <option key={team} value={team} className="bg-blue-950">
                      {team} ({teamGameCount} games)
                    </option>
                  );
                })}
              </select>
              {selectedTeam !== 'all' && (
                <button
                  onClick={() => setSelectedTeam('all')}
                  className="text-sm text-blue-300 hover:text-blue-200 underline"
                >
                  Clear filter
                </button>
              )}

              <label className="text-sm font-medium text-blue-200 ml-2">Filter by Club:</label>
              <select
                value={selectedClub}
                onChange={(e) => setSelectedClub(e.target.value)}
                className="bg-white/10 border border-white/20 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400/60"
              >
                <option value="all" className="bg-blue-950">All Clubs ({games.length} games)</option>
                {allClubs.map(club => {
                  const clubGameCount = games.filter(g => g.curlingClub === club).length;
                  return (
                    <option key={club} value={club} className="bg-blue-950">
                      {club} ({clubGameCount} games)
                    </option>
                  );
                })}
              </select>
              {selectedClub !== 'all' && (
                <button
                  onClick={() => setSelectedClub('all')}
                  className="text-sm text-blue-300 hover:text-blue-200 underline"
                >
                  Clear filter
                </button>
              )}

              {myLinkedNames.length > 0 && (
                <label className="flex items-center gap-2 text-sm font-medium text-blue-200 ml-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={myGamesOnly}
                    onChange={(e) => setMyGamesOnly(e.target.checked)}
                    className="rounded border-white/20 bg-white/10 text-blue-500 focus:ring-blue-400/60"
                  />
                  <User className="w-4 h-4 text-blue-300" />
                  My Games Only
                </label>
              )}
            </div>
          </div>
        )}

        {filteredGames.length === 0 && games.length > 0 ? (
          <div className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-2xl shadow-xl shadow-black/20 p-12 text-center">
            <Trophy className="w-16 h-16 text-blue-300/70 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No Games Found</h3>
            <p className="text-blue-300 mb-4">
              No games found
              {selectedTeam !== 'all' && <> for team &quot;{selectedTeam}&quot;</>}
              {selectedTeam !== 'all' && selectedClub !== 'all' && ' at'}
              {selectedClub !== 'all' && <> club &quot;{selectedClub}&quot;</>}
            </p>
            <button
              onClick={() => { setSelectedTeam('all'); setSelectedClub('all'); }}
              className="text-blue-300 hover:text-blue-200 underline"
            >
              View all games
            </button>
          </div>
        ) : filteredGames.length === 0 ? (
          <div className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-2xl shadow-xl shadow-black/20 p-12 text-center">
            <Trophy className="w-16 h-16 text-blue-300/70 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No Games Yet</h3>
            <p className="text-blue-300">Start tracking games to see them here</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredGames.map(game => {
              const winner = game.team1TotalScore > game.team2TotalScore
                ? game.team1Name
                : game.team2TotalScore > game.team1TotalScore
                ? game.team2Name
                : 'Tie';

              // Calculate shooting accuracy for each team
              const team1Shots = game.ends.flatMap(end =>
                end.shots.filter(shot => shot.team === 'team1')
              );
              const team2Shots = game.ends.flatMap(end =>
                end.shots.filter(shot => shot.team === 'team2')
              );

              // Traditional 0-4 shot-call scale, same weighting Player Summary uses.
              const weightedAccuracy = (shots: typeof team1Shots) => shots.length > 0
                ? (shots.reduce((sum, shot) => sum + getPointsFromResult(shot.result), 0) / (4 * shots.length)) * 100
                : 0;
              const team1Accuracy = weightedAccuracy(team1Shots);
              const team2Accuracy = weightedAccuracy(team2Shots);

              // Calculate weight and result counts
              const team1Heavy = team1Shots.filter(shot => shot.weight === 'heavy').length;
              const team1Light = team1Shots.filter(shot => shot.weight === 'light').length;
              const team1GoldStar = team1Shots.filter(shot => shot.isGoldStar).length;
              const team1Bombs = team1Shots.filter(shot => shot.isBomb).length;
              const team1HeavyPct = team1Shots.length > 0 ? (team1Heavy / team1Shots.length) * 100 : 0;
              const team1LightPct = team1Shots.length > 0 ? (team1Light / team1Shots.length) * 100 : 0;

              const team2Heavy = team2Shots.filter(shot => shot.weight === 'heavy').length;
              const team2Light = team2Shots.filter(shot => shot.weight === 'light').length;
              const team2GoldStar = team2Shots.filter(shot => shot.isGoldStar).length;
              const team2Bombs = team2Shots.filter(shot => shot.isBomb).length;
              const team2HeavyPct = team2Shots.length > 0 ? (team2Heavy / team2Shots.length) * 100 : 0;
              const team2LightPct = team2Shots.length > 0 ? (team2Light / team2Shots.length) * 100 : 0;

              const totalShots = team1Shots.length + team2Shots.length;
              const showAnalytics = totalShots > 0;
              const showTopButtons = showAnalytics;
              const sheetScoring = computeSheetScoring(game);
              const playerScores = computePlayerScores(game);
              const team1BrcScore = computeTeamScore(playerScores.filter(p => p.team === 'team1'));
              const team2BrcScore = computeTeamScore(playerScores.filter(p => p.team === 'team2'));

              return (
                <div key={game.id} className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-2xl shadow-xl shadow-black/20 p-6">
                  <div className="flex items-center justify-between gap-3 mb-1">
                    <div className="flex items-center gap-3">
                      <Calendar className="w-5 h-5 text-blue-300/70" />
                      <span className="text-sm text-blue-300">{formatDate(game.date)}</span>
                      {game.isComplete && (
                        <span className="px-2 py-1 bg-emerald-500/20 text-emerald-300 text-xs font-semibold rounded">
                          Completed
                        </span>
                      )}
                      {!game.isComplete && (
                        <span className="px-2 py-1 bg-yellow-500/20 text-yellow-300 text-xs font-semibold rounded">
                          In Progress
                        </span>
                      )}
                    </div>
                    {showAnalytics && (
                      <Link
                        to={`/CurlingCodex/game/${game.id}/brc-scoring`}
                        className="flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-400 text-white px-4 py-2 rounded-lg transition-colors w-64"
                      >
                        <Gauge className="w-4 h-4" />
                        <span>BRC Scoring</span>
                      </Link>
                    )}
                  </div>
                  {(game.curlingClub || game.sheetNumber) && (
                    <div className="flex items-center gap-2 mb-3">
                      <MapPin className="w-4 h-4 text-blue-300/70" />
                      <span className="text-sm text-blue-300">
                        {game.curlingClub}{game.curlingClub && game.sheetNumber && ' • '}{game.sheetNumber && `Sheet ${game.sheetNumber}`}
                        {sheetScoring.hasData && (
                          <>
                            {' • '}
                            <span className="text-white">Sheet Score: {sheetScoring.overallScore.toFixed(0)}%</span>
                          </>
                        )}
                      </span>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-8 mb-4">
                    <div>
                      <h3 className="flex items-center gap-2 font-semibold text-lg text-white mb-1">
                        {game.team1Name}
                        {game.isComplete && winner === game.team1Name && (
                          <Trophy className="w-4 h-4 text-yellow-400" />
                        )}
                      </h3>
                      <p className="text-3xl font-bold text-blue-300">{game.team1TotalScore}</p>
                      {team1Shots.length > 0 && (
                        <>
                          <p className="text-sm text-white mt-1">
                            Team Execution Score: {team1BrcScore.overallScore !== null ? `${team1BrcScore.overallScore.toFixed(0)}%` : '—'}
                            {' • '}Traditional Score: {team1Accuracy.toFixed(1)}%
                          </p>
                          <p className="text-xs text-white mt-1">
                            Heavy: {team1Heavy} ({team1HeavyPct.toFixed(1)}%) | Light: {team1Light} ({team1LightPct.toFixed(1)}%) | Gold Star: {team1GoldStar} | Bombs: {team1Bombs}
                          </p>
                        </>
                      )}
                    </div>
                    <div>
                      <h3 className="flex items-center gap-2 font-semibold text-lg text-white mb-1">
                        {game.team2Name}
                        {game.isComplete && winner === game.team2Name && (
                          <Trophy className="w-4 h-4 text-yellow-400" />
                        )}
                      </h3>
                      <p className="text-3xl font-bold text-emerald-300">{game.team2TotalScore}</p>
                      {team2Shots.length > 0 && (
                        <>
                          <p className="text-sm text-white mt-1">
                            Team Execution Score: {team2BrcScore.overallScore !== null ? `${team2BrcScore.overallScore.toFixed(0)}%` : '—'}
                            {' • '}Traditional Score: {team2Accuracy.toFixed(1)}%
                          </p>
                          <p className="text-xs text-white mt-1">
                            Heavy: {team2Heavy} ({team2HeavyPct.toFixed(1)}%) | Light: {team2Light} ({team2LightPct.toFixed(1)}%) | Gold Star: {team2GoldStar} | Bombs: {team2Bombs}
                          </p>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="text-sm text-blue-300 mb-4">
                    {game.ends.length} ends • {totalShots} shots recorded
                  </div>

                  {/* Action Buttons */}
                  {showTopButtons && (
                    <>
                      <div className="grid grid-cols-3 gap-2">
                        <Link
                          to={`/CurlingCodex/game/${game.id}/sheet-map`}
                          className="flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-400 text-white px-4 py-2 rounded-lg transition-colors"
                        >
                          <Map className="w-4 h-4" />
                          <span>Sheet Map</span>
                        </Link>
                        <Link
                          to={`/CurlingCodex/game/${game.id}/rock-map`}
                          className="flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-400 text-white px-4 py-2 rounded-lg transition-colors"
                        >
                          <Target className="w-4 h-4" />
                          <span>Rock Map</span>
                        </Link>
                        <Link
                          to={`/CurlingCodex/game/${game.id}/player-summary`}
                          className="flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-400 text-white px-4 py-2 rounded-lg transition-colors"
                        >
                          <Users className="w-4 h-4" />
                          <span>Player Summary</span>
                        </Link>
                      </div>
                    </>
                  )}

                  {showTopButtons && <div className="border-t border-white/10 my-3" />}

                  <div className="grid grid-cols-2 gap-2">
                    {!game.isComplete ? (
                      <Link
                        to={`/CurlingCodex/game/${game.id}`}
                        className="flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-400 text-white px-4 py-2 rounded-lg transition-colors"
                      >
                        <Play className="w-4 h-4" />
                        <span>Resume Game</span>
                      </Link>
                    ) : (
                      <Link
                        to={`/CurlingCodex/game/${game.id}`}
                        className="flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-400 text-white px-4 py-2 rounded-lg transition-colors"
                      >
                        <Eye className="w-4 h-4" />
                        <span>View Game</span>
                      </Link>
                    )}
                    <button
                      onClick={() => handleDeleteGame(game.id)}
                      className="flex items-center justify-center gap-2 bg-red-500/80 hover:bg-red-500 text-white px-4 py-2 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                      <span>Delete Game</span>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}